import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionSuccess = ({ token }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && token) {
      checkPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, [searchParams, token]);

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/subscription/checkout-status/${sessionId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setPaymentDetails(response.data);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
      } else {
        setStatus('pending');
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-lg font-body text-muted-foreground">Confirming your payment...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md text-center">
          <div className="h-16 w-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <span className="text-4xl">❌</span>
          </div>
          <h1 className="text-3xl font-heading font-semibold mb-4">Payment Error</h1>
          <p className="text-base font-body text-muted-foreground mb-6">
            We couldn't verify your payment. Please contact support if you were charged.
          </p>
          <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center" data-testid="success-page">
      <div className="max-w-md text-center">
        <div className="h-20 w-20 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="h-12 w-12 text-green-600" />
        </div>
        
        <h1 className="text-4xl font-heading font-semibold mb-4">
          Welcome to Premium!
        </h1>
        
        <p className="text-lg font-body text-muted-foreground mb-8">
          Your subscription is now active. You have full access to all premium features.
        </p>

        {paymentDetails && (
          <div className="bg-card border border-border rounded-sm p-6 mb-8 text-left">
            <h3 className="text-lg font-heading font-medium mb-4">Payment Details</h3>
            <div className="space-y-2 text-sm font-body">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Amount:</span>
                <span className="font-mono font-medium">${(paymentDetails.amount_total / 100).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status:</span>
                <span className="text-green-600 font-medium">Paid</span>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <Button onClick={() => navigate('/dashboard')} className="w-full" size="lg">
            Go to Dashboard
          </Button>
          <Button onClick={() => navigate('/settings')} variant="outline" className="w-full">
            View Billing Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
